import React, {useMemo, useState} from "react";
import {Box, Stack} from "@mui/material";
import {AudioWave01Icon, CallIcon, ChatBotIcon, Mic01Icon} from "@hugeicons/core-free-icons";
import {useRegistryProject} from "../RegistryProjectContext";
import {useSubProjectFlows} from "../useSubProjectFlows";
import {ACTIVE_CALLS, useProjectStatistics} from "./useProjectStatistics";
import {DashboardHeading, DEFAULT_REFRESH_INTERVAL} from "./components/DashboardHeading";
import {ActiveCallsCard, ResponseTimeCard} from "./components/ResponseTimeCard";
import {ConversationsPanel} from "./components/ConversationsPanel";
import {TagUsagePanel} from "./components/TagUsagePanel";
import {StatusPanel} from "./components/StatusPanel";
import {ActivityPanel} from "./components/ActivityPanel";
import {SubProjectFilter, VersionTypeSwitch} from "../SubProjectSelect";

const RESPONSE_TIME_METRICS = [
  {type: "TEXT_TO_SPEECH", label: "Synthesize response", icon: AudioWave01Icon, color: "#8491F7"},
  {type: "SPEECH_TO_TEXT", label: "Transcribe response", icon: Mic01Icon, color: "#6FA5CC"},
  {type: "CHATBOT", label: "Chatbot response", icon: ChatBotIcon, color: "#A49BDA"},
];

// Project type -> conversation type of the statistics
const CONVERSATION_TYPES = {VOICE_ASSISTANT: "VOICE_BOT", CHATBOT: "CHAT_BOT"};

// Default page of a registry project: overview of the PRODUCTION (or STAGE) version, over every language or the one
// picked in the heading (sent as projectVersionType / subProjectId to every statistic and the conversations)
export const RegistryDashboardPage = () => {
  const {project, subProject: urlSubProject, changeSubProject} = useRegistryProject();
  // null = every language; picking one navigates to its own /{projectId}/{subProjectId} URL (changeSubProject),
  // same as the Designer/Dialplan switcher - "every language" has no URL of its own, so it stays local-only.
  // Starts on every language: the URL always has a sub project (the first one by default), so the user picks one.
  const [showAllSubProjects, setShowAllSubProjects] = useState(true);
  const subProject = showAllSubProjects ? null : urlSubProject;
  const handleSubProjectChange = (value) => {
    if (value) {
      setShowAllSubProjects(false);
      changeSubProject(value);
    } else {
      setShowAllSubProjects(true);
    }
  };
  // PRODUCTION / STAGE
  const [projectVersionType, setProjectVersionType] = useState("PRODUCTION");

  // Firebase flows of every language: tag names, success tags and phonebooks for the conversations and tag usage
  const {flows, tagNames} = useSubProjectFlows(project);

  const [refreshInterval, setRefreshInterval] = useState(DEFAULT_REFRESH_INTERVAL);
  const statisticFilters = useMemo(() => ({
    subProjectId: subProject?.id,
    projectVersionType: projectVersionType || undefined,
    conversationType: CONVERSATION_TYPES[project.type],
  }), [subProject?.id, projectVersionType, project.type]);
  const statistics = useProjectStatistics({
    projectId: project.id, flowUid: subProject?.flowUid, filters: statisticFilters, refreshInterval
  });

  const conversationType = CONVERSATION_TYPES[project.type];

  return (
    <Box sx={{flex: 1, minHeight: 0, display: "flex", gap: 2, px: 2, py: 1, bgcolor: "#F7F7FB", overflowY: "auto"}}>
      <Stack gap={2} sx={{
        p: {xs: 1, md: 2},
        maxWidth: {xs: "calc(100% - 16px)", md: "calc(100% - 128px)"},
        width: "100%",
        mx: "auto",
        pb: "100px",
        // Sections keep their content height; the outer box scrolls instead of the column squeezing them
        "& > *": {flexShrink: 0},
      }}>
        <DashboardHeading
          title={project.type === "CHATBOT" ? "Chat overview" : "Voice overview"}
          refreshInterval={refreshInterval}
          onChangeRefreshInterval={setRefreshInterval}
          lastFetched={statistics.lastFetched}
          actions={<>
            <SubProjectFilter value={subProject?.id} onChange={handleSubProjectChange}/>
            <VersionTypeSwitch value={projectVersionType} onChange={setProjectVersionType}/>
          </>}
        />

        <Box component={"section"} aria-label={"Live calls and response performance"} sx={{
          display: "grid", gap: 2,
          gridTemplateColumns: {xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))"},
        }}>

          {RESPONSE_TIME_METRICS.map(metric => (
            <ResponseTimeCard
              key={metric.type}
              {...metric}
              statistic={statistics.responseTime[metric.type]}
              loading={statistics.responseTimeLoading[metric.type]}
              window={statistics.responseTimeWindows[metric.type]}
              onChangePeriod={statistics.setResponseTimePeriod}
              onShift={statistics.shiftResponseTime}
            />
          ))}
          <ActiveCallsCard
            type={ACTIVE_CALLS}
            label={project.type === "CHATBOT" ? "Active conversations" : "Active calls"}
            icon={CallIcon}
            color={"#8491F7"}
            activeCalls={statistics.activeCalls}
            statistic={statistics.responseTime[ACTIVE_CALLS]}
            window={statistics.responseTimeWindows[ACTIVE_CALLS]}
            onChangePeriod={statistics.setResponseTimePeriod}
            onShift={statistics.shiftResponseTime}
          />
        </Box>

        {/* Flex row: both panels stretch to the height of the taller one (stacked below lg) */}
        <Box component={"section"} aria-label={"Recent conversations and tag usage"} sx={{
          display: "flex", flexDirection: {xs: "column", lg: "row"}, alignItems: "stretch", gap: 2,
        }}>
          <Box sx={{flex: {lg: "2 1 0"}, minWidth: 0, display: "flex", "& > *": {flex: 1}}}>
            <ConversationsPanel
              projectId={project.id}
              subProjectId={subProject?.id}
              projectVersionType={projectVersionType}
              subProjects={project.subProjects}
              flows={flows}
              conversationType={conversationType}
              refreshInterval={refreshInterval}
            />
          </Box>
          <Box sx={{flex: {lg: "1 1 0"}, minWidth: 0, display: "flex", "& > *": {flex: 1}}}>
            <TagUsagePanel
              projectId={project.id}
              filters={statisticFilters}
              tagNames={tagNames}
              refreshInterval={refreshInterval}
            />
          </Box>
        </Box>

        {/* Outcomes (statuses) and activity (daily calls), stretched to the same height */}
        <Box component={"section"} aria-label={"Conversation insights"} sx={{
          display: "flex", flexDirection: {xs: "column", lg: "row"}, alignItems: "stretch", gap: 2,
          pb: "150px",
        }}>
          <Box sx={{flex: {lg: "2 1 0"}, minWidth: 0, display: "flex", "& > *": {flex: 1}}}>
            <StatusPanel projectId={project.id} filters={statisticFilters} refreshInterval={refreshInterval}/>
          </Box>
          <Box sx={{flex: {lg: "3 1 0"}, minWidth: 0, display: "flex", "& > *": {flex: 1}}}>
            <ActivityPanel
              projectId={project.id}
              filters={statisticFilters}
              refreshInterval={refreshInterval}
              conversationLabel={conversationType === "CHAT_BOT" ? "Conversations" : "Calls"}
            />
          </Box>
        </Box>

      </Stack>
    </Box>
  );
};
